import {
  Box,
  Button,
  FormControl,
  Grid,
  IconButton,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { Activity } from "../../../../models/Activities";
import FileUtils from "../../../../utils/fileUtils";
import { useEffect, useState } from "react";
import TableApprovalStatus from "./TableApprovalStatus";
import { SubOrganizations } from "../../../../models/SubOrganization";
import CustomInput from "../../../../components/input/CustomInput";
import dayjs, { Dayjs } from "dayjs";
import { Controller, useForm } from "react-hook-form";
import ActivitiesService from "../../../../service/activities.service";
import alertService from "../../../../components/alert/alertService";
import CustomModal from "../../../../components/modal/CustomModal";
import { INPUT_AUXILIARY, InputAuxiliary } from "../../../../constants/form";
import { useAuth } from "../../../../contexts/UserContext";
import { DataEntryRoleContext } from "../../../../contexts/DataEntryRoleContext";
import { common, input } from "../../../../i18n";
import { OrganizationBreadcrumbs } from "../OrganizationBreadcrumbs";
import { useCat3UpstreamPublisher } from "../useCat3UpstreamPublisher";

interface Props {
  data: Activity;
  subOrganizationList: SubOrganizations[];
  open: boolean;
  onClose: () => void;
  type: string; // view , approval
}

type FormValues = {
  comment: string;
};

export const ReviewApproval = (props: Props) => {
  const { t, i18n } = useTranslation([common, input], {
    nsMode: "fallback",
  });

  const { organization } = useAuth();

  const [file, setFile] = useState<File | null>(null);

  const [location, setLocation] = useState<string>("");

  const [dateOfActivity, setDateOfActivity] = useState<string>("");

  const { updateActivitiesAfterEdit } = DataEntryRoleContext();

  const [inputAuxiliary, setInputAuxiliary] = useState<InputAuxiliary>();

  const upstreamPublisher = useCat3UpstreamPublisher(props.open, props.data);

  const getActivityAttachment = async (refId: string) => {
    try {
      if (props.data.attachment !== "") {
        const fileBase64 = await ActivitiesService.getAttachmentMeta(refId);
        if (fileBase64?.attachment) {
          setFile(FileUtils.base64ToFile(fileBase64.attachment ?? ""));
        }
      }
    } catch (error) {
      setFile(null);
    }

    return file;
  };

  useEffect(() => {
    if (props.open && props.data) {
      getActivityAttachment(props.data._id);

      setInputAuxiliary(
        INPUT_AUXILIARY.find(
          (e) => e.emissionGroup === props.data.emissionGroup,
        ),
      );

      const findResult = props.subOrganizationList.find(
        (i) => props.data.suborganizationName === i.name,
      );

      const location = `${findResult?.location?.address ?? ""} ${
        findResult?.location?.district ?? ""
      } ${findResult?.location?.city ?? ""}`.trim();

      setLocation(location);

      const start: Dayjs = dayjs(props.data.startDate);

      const end: Dayjs = dayjs(props.data.endDate);

      if (start.month === end.month) {
        setDateOfActivity(start.format("MMM YYYY"));
      } else {
        setDateOfActivity(
          start.format(
            `${start.format("MMM YYYY")} - ${end.format("MMM YYYY")}`,
          ),
        );
      }
    }
  }, [props.open]);

  const close = () => {
    setFile(null);

    setLocation("");

    setDateOfActivity("");

    control._reset();

    props.onClose();
  };

  const { control, handleSubmit } = useForm<FormValues>({
    defaultValues: {
      comment: "",
    },
  });

  const onSubmit = async (data: FormValues, event: any) => {
    let result;
    try {
      result = await ActivitiesService.updateStatus(
        props.data._id,
        event.nativeEvent.submitter.name === "approve"
          ? "approved"
          : "rejected",
        dayjs(props.data.startDate).year(),
      );
    } catch {
      // error alert already shown by service
      return;
    }

    if (result) {
      // setUpActivitiesListData(result);

      await updateActivitiesAfterEdit();

      alertService.mixin(
        event.nativeEvent.submitter.name === "approve"
          ? t("Emission-Approved-Success")
          : t("Emission-Rejected-Success"),
      );

      close();
    }
  };

  return (
    <>
      <CustomModal
        body={
          <form onSubmit={handleSubmit(onSubmit)}>
            <Grid container width="100%" height={"70dvh"}>
              <Grid
                item
                xs={12}
                md={6}
                padding={2}
                display={"flex"}
                flexDirection={"column"}
                rowGap={2.5}
                height={"100%"}
                bgcolor={"var(--gray8)"}
              >
                <Typography variant="h3" align="left">
                  {t("Preview-Evidence")}
                </Typography>

                {file !== null && file.type.startsWith("image/") ? (
                  <iframe
                    title={props.data._id}
                    width="100%"
                    height="100%"
                    allowFullScreen
                    frameBorder="0"
                    style={{
                      backgroundColor: "var(--gray3)",
                      border: "0px !important",
                      width: "100%",
                      height: "100%",
                      textAlign: "center",
                    }}
                    loading="eager"
                    srcDoc={`
                        <html>
                          <head>
                            <style>
                              body {
                                margin: 0;
                                display: flex;
                                justify-content: center;
                                align-items: center;
                                height: 100%;
                              }
                              img {
                                width: 100%;
                                height: auto;
                              }
                            </style>
                          </head>
                   <body>
                  <img src="${
                    URL.createObjectURL(file) ?? ""
                  }" alt="Selected Image" />
                </body>
                        </html>
                      `}
                  ></iframe>
                ) : (
                  file && (
                    <Box width={"100%"} height={"100%"} alignContent={"center"}>
                      {FileUtils.canPreviewInline(file.type) ? (
                        <iframe
                          width="100%"
                          height="100%"
                          title="null"
                          loading="eager"
                          frameBorder="0"
                          src={URL.createObjectURL(file)}
                        ></iframe>
                      ) : (
                        <Typography variant="h3" textAlign={"center"}>
                          {t("Cannot-Preview-Evidence")}
                        </Typography>
                      )}
                    </Box>
                  )
                )}

                {file ? (
                  <Box
                    display={"flex"}
                    flexDirection={"row"}
                    alignItems={"center"}
                    bgcolor={"var(--white)"}
                    borderRadius={"4px"}
                    paddingY={"8px"}
                    paddingX={"12px"}
                    onClick={() => {
                      if (FileUtils.canOpenInNewTab(file.type)) {
                        window.open(URL.createObjectURL(file));
                      } else {
                        alertService.error(t("Cannot-Preview-Evidence"));
                      }
                    }}
                    sx={{
                      cursor: "pointer",
                      ":hover": {
                        opacity: 0.75,
                      },
                    }}
                  >
                    <Typography
                      flex={1}
                      variant="h3"
                      align="left"
                      sx={{ overflow: "hidden", textOverflow: "ellipsis" }}
                    >
                      {file?.name}
                    </Typography>

                    <IconButton>
                      <img src="/img/export.svg" alt="export" />
                    </IconButton>
                  </Box>
                ) : null}
              </Grid>
              <Grid
                item
                xs={12}
                md={6}
                padding={2}
                height={"100%"}
                display="flex"
                flexDirection={"column"}
                rowGap={"20px"}
              >
                <Box display={"flex"} flexDirection={"row"} gap={"8px"}>
                  <Typography align="left" variant="h3" color={"var(--dark)"}>
                    {t("Review")} EM-{props.data.id}
                  </Typography>

                  <TableApprovalStatus status={props.data.status} by="" />
                </Box>

                <OrganizationBreadcrumbs
                  organization={organization?.name ?? ""}
                  location={location}
                  subOrganizations={props.data.suborganizationName}
                  site={props.subOrganizationList
                    .find((i) => i.name === props.data.suborganizationName)
                    ?.sites.find((site) => site.name === props.data.siteName)}
                />

                <Box
                  display={"flex"}
                  flexDirection={"column"}
                  rowGap={"20px"}
                  paddingY={"24px"}
                  textAlign={"left"}
                  flex={1}
                  paddingX={0.8}
                  overflow={"auto"}
                >
                  <Typography variant="h2" color={"var(--dark)"} align="left">
                    {t(props.data.emissionGroup)}
                    {/* <InfoPopper
                      text={t(props.data.emissionGroup + "-Description")}
                    /> */}
                  </Typography>
                  <CustomInput
                    label={t("Date-of-Activity")}
                    info={t("Date-of-Activity")}
                    value={dateOfActivity}
                    required
                    displayOnly
                    disabled
                  />
                  <CustomInput
                    label={t("Asset-Type")}
                    info={t("Asset-Type-Description")}
                    value={props.data.assetType}
                    required
                    disabled
                    displayOnly
                  />
                  <CustomInput
                    label={t("Asset-Name")}
                    info={t("Asset-Name-Description")}
                    required
                    value={props.data.assetName}
                    disabled
                    displayOnly
                  />
                  <CustomInput
                    label={t("Activity-Group")}
                    info={t("Activity-Group")}
                    value={
                      i18n.language === "th"
                        ? props.data.activityGroupTh
                        : props.data.activityGroupEN
                    }
                    disabled
                    displayOnly
                  />
                  <CustomInput
                    label={t("Activity-Type")}
                    info={t("Activity-Type")}
                    value={
                      i18n.language === "th"
                        ? props.data.activityTypeTh
                        : props.data.activityTypeEn
                    }
                    required
                    disabled
                    displayOnly
                  />
                  <CustomInput
                    label={t("Activity-Attribute")}
                    info={t("Activity-Attribute")}
                    value={
                      i18n.language === "th"
                        ? props.data.activityAttributeTh
                        : props.data.activityAttributeEN
                    }
                    required
                    disabled
                    displayOnly
                  />
                  <Box>
                    <CustomInput
                      label={t("Activity-Emission-Factor-Publisher")}
                      info={t("Activity-Emission-Factor-Publisher")}
                      value={props.data.publishReference || props.data.publisherName}
                      required
                      disabled
                      displayOnly
                    />
                    {upstreamPublisher && (
                      <Typography
                        component="span"
                        display="block"
                        variant="body2"
                        color="var(--gray9)"
                        lineHeight="16px"
                        mt="6px"
                        data-testid="ef-upstream-hint-review"
                      >
                        {t("Activity-Emission-Factor-Upstream-Hint", {
                          publisher: upstreamPublisher,
                        })}
                      </Typography>
                    )}
                  </Box>
                  <CustomInput
                    label={t("Vendor")}
                    info={t("Vendor")}
                    value={props.data.vendor ?? ""}
                    disabled
                    displayOnly
                  />
                  <CustomInput
                    label={t("Unit")}
                    info={t("Unit")}
                    value={props.data.unit}
                    required
                    disabled
                    displayOnly
                  />
                  <CustomInput
                    label={t(inputAuxiliary?.amount.label ?? "")}
                    info={t(inputAuxiliary?.amount.info ?? "")}
                    // label={t(inputAuxiliary?.amount.label ?? "")}
                    // info={t(inputAuxiliary?.amount.info ?? "")}
                    value={`${props.data.amount}`}
                    required
                    disabled
                    displayOnly
                  />
                  {inputAuxiliary?.auxiliary?.map((e, i) => {
                    const item = props?.data?.auxiliary ?? null;

                    let result: any = JSON.stringify(item);

                    result = JSON.parse(result);

                    return e.label !== "" ? (
                      <CustomInput
                        label={t(e.label)}
                        info={t(e.info)}
                        value={result[`aux${i + 1}`]}
                        required
                        disabled
                        displayOnly
                      />
                    ) : null;
                  })}
                </Box>

                {props.type === "approval" ? (
                  <Box
                    display={"flex"}
                    flexDirection={"column"}
                    rowGap={"20px"}
                  >
                    <FormControl fullWidth>
                      <Controller
                        name="comment"
                        control={control}
                        defaultValue=""
                        render={({ field: { onChange, value } }) => (
                          <CustomInput
                            label={t("Comment")}
                            info={t("Comment")}
                            value={value}
                            handleOnChange={onChange}
                          />
                        )}
                      />
                    </FormControl>

                    <Button
                      fullWidth
                      color="secondary"
                      variant="contained"
                      type="submit"
                      name="approve"
                    >
                      {t("Approve")}
                    </Button>

                    <Button
                      color="secondary"
                      variant="outlined"
                      type="submit"
                      name="reject"
                    >
                      {t("Reject")}
                    </Button>
                  </Box>
                ) : null}

                {props.type === "view" ? (
                  <Box
                    display={"flex"}
                    flexDirection={"column"}
                    rowGap={"20px"}
                  >
                    <Button
                      color="secondary"
                      variant="outlined"
                      onClick={() => close()}
                    >
                      {t("Close")}
                    </Button>
                  </Box>
                ) : null}
              </Grid>
            </Grid>
          </form>
        }
        open={props.open}
        onClose={() => close()}
      />
    </>
  );
};
